import { Check, Minus } from 'lucide-react';
import { useId } from 'react';
import { answerHeard } from '../../shared/answers.ts';
import { ASSESSMENT_KINDS } from '../../shared/assessment.ts';
import type { UserMemory } from '../../shared/memory.ts';
import { speakingTask } from '../../shared/registry/speaking-task.ts';
import type { TaskKind } from '../../shared/tasks.ts';
import { cn } from '../lib/cn.ts';
import type { CanvasItem } from '../session/conversation.ts';

export type StepState = 'done' | 'skipped' | 'todo';
export type WarmupStep = { kind: TaskKind; state: StepState };

// "Warm-up", never test or assessment (ADR-0012): the set should feel like practice.
const LABEL: Record<TaskKind, string> = {
  free_speech: 'Intro',
  read_aloud: 'Read aloud',
  interview_answer: 'Question',
};
const SAID: Record<StepState, string> = { done: 'done', skipped: 'skipped', todo: 'to do' };

/**
 * Where the assessment set stands: what memory has recorded, plus this call's own tasks, which reach memory only
 * when the answer is analysed (answers) or the call completes (skips). This call's tasks count as the server counts
 * them: an answer it heard (or, with no review, scored), and a skip the user tapped. An answer anywhere beats a skip.
 */
export function warmupSteps(
  memory: UserMemory,
  items: CanvasItem[] = [],
  // What's recorded so far: call 2's set, or a check-in's round (flow 002).
  record: Record<string, { status: string } | undefined> = memory.assessment.items,
): WarmupStep[] {
  const inCall = new Map<TaskKind, StepState>();
  for (const item of items) {
    const kind = item.params.kind as TaskKind | undefined;
    const t = item.task;
    if (item.tool !== speakingTask.name || !kind || !t) continue;
    if (t.phase === 'done' && answerHeard(t.feedback, t.scores)) inCall.set(kind, 'done');
    else if (t.phase === 'skipped' && t.skippedByUser && !inCall.has(kind)) inCall.set(kind, 'skipped');
  }
  return ASSESSMENT_KINDS.map((kind) => {
    const saved = record[kind]?.status;
    // A status from a newer build counts as present, as it does for what's left (assessmentLeft).
    const state: StepState =
      inCall.get(kind) === 'done' || (saved && saved !== 'skipped')
        ? 'done'
        : saved === 'skipped' || inCall.has(kind)
          ? 'skipped'
          : 'todo';
    return { kind, state };
  });
}

/** The steps; `compact` is the call header's version, labelled, since there's no copy around it to explain it. */
export function WarmupSteps({
  steps,
  compact = false,
  label = 'Warm-up',
}: {
  steps: WarmupStep[];
  compact?: boolean;
  label?: string;
}) {
  const labelId = useId();
  return (
    <div
      data-testid="warmup"
      data-settled={steps.filter((s) => s.state !== 'todo').length}
      className={cn('flex flex-wrap items-center justify-center gap-x-3 gap-y-1', compact ? 'text-xs' : 'text-sm')}
    >
      <span id={labelId} className={compact ? 'text-subtle' : 'sr-only'}>
        {label}
      </span>
      {/* role: Safari drops list semantics from an unstyled list. */}
      {/* biome-ignore lint/a11y/noRedundantRoles: see above */}
      <ol role="list" aria-labelledby={labelId} className="flex items-center gap-3">
        {steps.map((s) => (
          <li key={s.kind} data-testid={`warmup-${s.kind}`} data-state={s.state} className="flex items-center gap-1.5">
            <span
              aria-hidden
              className={cn(
                'grid place-items-center rounded-full ring-1',
                compact ? 'size-4' : 'size-5',
                s.state === 'done' && 'bg-good/15 text-good ring-good/30',
                s.state === 'skipped' && 'bg-fg/5 text-subtle ring-fg/15',
                s.state === 'todo' && 'ring-fg/25',
              )}
            >
              {s.state === 'done' ? <Check className="size-3" /> : null}
              {s.state === 'skipped' ? <Minus className="size-3" /> : null}
            </span>
            <span className={s.state === 'todo' ? 'text-fg/80' : 'text-subtle'}>
              {LABEL[s.kind]}
              <span className="sr-only">, {SAID[s.state]}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
